import {
  formatAmount,
  formatDateTime,
  isAbortError,
  optionalValue,
} from "~/common/utils"
import { Text } from "~/components/Text"
import { useEffect, useRef, useState } from "react"
import type { Dayjs } from "dayjs"
import { CodeOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Table,
  Tag,
  Typography,
  message,
} from "antd"
import type { DescriptionsProps, TableProps } from "antd"
import { getStaffRawTaskDetail, listStaffFrameCopyTasks } from "~/api/staff"
import { ApiRequestError } from "~/common/request"
import {
  FrameCopyStatus,
  ImageTaskStatus,
  TaskStepStatus,
} from "~/common/constants"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Link, Paragraph, Title } = Typography
const { RangePicker } = DatePicker

type FrameCopyTaskFilterForm = {
  createdRange?: [Dayjs, Dayjs]
  userId?: string
}

type RawTaskModalState = {
  stepLabel: string
  rawTask: API.Staff.StaffRawTask
}

type StepStatusConfig = {
  color: string
  label: string
}

const taskStepLabel: Record<API.Staff.RawTaskStep, string> = {
  "": "未指定",
  white_image: "白底图",
  nine_grid_image: "九宫格图",
  parse_video: "视频解析",
  optimize_prompt: "提示词优化",
  create_video: "视频生成",
  blend_frame: "分镜融合",
  blend_frame_artline: "分镜线稿",
  generate_scene: "场景生成",
  blend_prompt: "提示词融合",
  parse_dialog: "对话解析",
}

const frameCopyStatusLabel: Record<FrameCopyStatus, string> = {
  [FrameCopyStatus.init]: "初始化",
  [FrameCopyStatus.blendFrame]: "分镜融合中",
  [FrameCopyStatus.blendFrameFailed]: "分镜融合失败",
  [FrameCopyStatus.blendFrameDone]: "分镜融合完成",
  [FrameCopyStatus.blendPrompt]: "提示词融合中",
  [FrameCopyStatus.blendPromptFailed]: "提示词融合失败",
  [FrameCopyStatus.blendPromptDone]: "提示词融合完成",
  [FrameCopyStatus.createVideo]: "视频生成中",
  [FrameCopyStatus.createVideoFailed]: "视频生成失败",
  [FrameCopyStatus.createVideoDone]: "视频生成完成",
  [FrameCopyStatus.copy]: "复制任务临时状态",
  [FrameCopyStatus.parseDialog]: "对话解析中",
  [FrameCopyStatus.parseDialogDone]: "对话解析完成",
  [FrameCopyStatus.parseDialogFailed]: "对话解析失败",
}

const stepStatusConfig: Record<TaskStepStatus, StepStatusConfig> = {
  [TaskStepStatus.pending]: { color: "default", label: "等待中" },
  [TaskStepStatus.processing]: { color: "processing", label: "处理中" },
  [TaskStepStatus.succeeded]: { color: "success", label: "成功" },
  [TaskStepStatus.failed]: { color: "error", label: "失败" },
}

const imageStatusConfig: Record<ImageTaskStatus, StepStatusConfig> = {
  [ImageTaskStatus.pending]: { color: "default", label: "等待中" },
  [ImageTaskStatus.created]: { color: "blue", label: "已创建" },
  [ImageTaskStatus.running]: { color: "processing", label: "执行中" },
  [ImageTaskStatus.succeeded]: { color: "success", label: "成功" },
  [ImageTaskStatus.failed]: { color: "error", label: "失败" },
  [ImageTaskStatus.cancelled]: { color: "warning", label: "已取消" },
}

const costStatusConfig: Record<API.Staff.RawTaskCostStatus, StepStatusConfig> =
  {
    1: { color: "success", label: "计算成功" },
    2: { color: "error", label: "计算失败" },
  }

const formatCost = (cost?: number | null) => {
  return typeof cost === "number" ? `${formatAmount(cost)}（${cost} 分）` : "-"
}

const formatJson = (value: unknown) => {
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }

  try {
    return JSON.stringify(value, null, 2) ?? String(value)
  } catch {
    return String(value)
  }
}

const renderId = (value?: string | null) => {
  if (!value) return "-"

  return (
    <Paragraph
      className={styles.copyableText}
      copyable={{ text: value, tooltips: ["复制", "已复制"] }}
      ellipsis={{ tooltip: value }}
    >
      {value}
    </Paragraph>
  )
}

const renderStatusTag = (
  status: number,
  config: Partial<Record<number, StepStatusConfig>>,
) => {
  const matched = config[status]
  return <Tag color={matched?.color}>{matched?.label || `状态 ${status}`}</Tag>
}

const renderFrameCopyStatus = (status: FrameCopyStatus) => {
  const failed = [
    FrameCopyStatus.blendFrameFailed,
    FrameCopyStatus.blendPromptFailed,
    FrameCopyStatus.createVideoFailed,
  ].includes(status)
  const done = [
    FrameCopyStatus.blendFrameDone,
    FrameCopyStatus.blendPromptDone,
    FrameCopyStatus.createVideoDone,
  ].includes(status)
  const color = failed ? "error" : done ? "success" : "processing"

  return <Tag color={color}>{frameCopyStatusLabel[status] || status}</Tag>
}

const StepCell = ({
  errorMessage,
  meta,
  rawTaskId,
  rawTaskLoading,
  status,
  statusType,
  onOpenRawTask,
}: {
  errorMessage?: string
  meta: string[]
  rawTaskId?: string | null
  rawTaskLoading?: boolean
  status: number
  statusType: "image" | "step"
  onOpenRawTask?: (rawTaskId: string) => void
}) => {
  return (
    <Flex
      className={styles.stepCell}
      vertical
      gap={4}
    >
      {renderStatusTag(
        status,
        statusType === "image" ? imageStatusConfig : stepStatusConfig,
      )}
      {meta.filter(Boolean).map((item) => (
        <Text
          size="s"
          className={styles.stepMeta}
          ellipsis={{ tooltip: item }}
          key={item}
          type="secondary"
        >
          {item}
        </Text>
      ))}
      {errorMessage ? (
        <Text
          size="s"
          className={styles.stepMeta}
          ellipsis={{ tooltip: errorMessage }}
          type="danger"
        >
          {errorMessage}
        </Text>
      ) : null}
      {rawTaskId && onOpenRawTask ? (
        <Button
          className={styles.rawTaskButton}
          loading={rawTaskLoading}
          size="small"
          type="link"
          onClick={() => onOpenRawTask(rawTaskId)}
        >
          查看 RawTask
        </Button>
      ) : null}
    </Flex>
  )
}

const RawTaskModal = ({
  modalState,
  onClose,
}: {
  modalState: RawTaskModalState | null
  onClose: () => void
}) => {
  const rawTask = modalState?.rawTask
  const costStatus = rawTask?.cost_status
    ? costStatusConfig[rawTask.cost_status]
    : undefined
  const descriptionItems: DescriptionsProps["items"] = rawTask
    ? [
        { key: "id", label: "RawTask ID", children: renderId(rawTask.id) },
        {
          key: "created_t",
          label: "创建时间",
          children: formatDateTime(rawTask.created_t),
        },
        {
          key: "user_id",
          label: "用户 ID",
          children: renderId(rawTask.user_id),
        },
        {
          key: "task_id",
          label: "关联任务 ID",
          children: renderId(rawTask.task_id),
        },
        {
          key: "task_from",
          label: "任务来源",
          children: `类型 ${rawTask.task_from}`,
        },
        {
          key: "task_step",
          label: "任务步骤",
          children:
            taskStepLabel[rawTask.task_step] || rawTask.task_step || "-",
        },
        {
          key: "cost_status",
          label: "成本状态",
          children: costStatus ? (
            <Tag color={costStatus.color}>{costStatus.label}</Tag>
          ) : (
            "未计算"
          ),
        },
        {
          key: "cost",
          label: "实际成本",
          children: formatCost(rawTask.cost),
        },
        ...(rawTask.cost_error
          ? [
              {
                key: "cost_error",
                label: "成本计算错误",
                children: <Text type="danger">{rawTask.cost_error}</Text>,
                span: 2,
              },
            ]
          : []),
      ]
    : []

  return (
    <Modal
      footer={null}
      open={Boolean(rawTask)}
      title={`RawTask 详情${modalState ? ` · ${modalState.stepLabel}` : ""}`}
      width={920}
      onCancel={onClose}
    >
      {rawTask ? (
        <Flex
          vertical
          gap={18}
        >
          <Descriptions
            bordered
            column={2}
            items={descriptionItems}
            size="small"
          />
          {rawTask.cost_detail ? (
            <section>
              <Title level={5}>成本审计明细</Title>
              <pre className={styles.jsonBlock}>
                {formatJson(rawTask.cost_detail)}
              </pre>
            </section>
          ) : null}
          <section>
            <Title level={5}>detail_zip</Title>
            <pre className={styles.jsonBlock}>
              {formatJson(rawTask.detail_zip)}
            </pre>
          </section>
        </Flex>
      ) : null}
    </Modal>
  )
}

export const StaffFrameCopyTaskList = () => {
  const [form] = Form.useForm<FrameCopyTaskFilterForm>()
  const { total, loading, search, refresh, tableProps } = usePagedQuery<
    Pick<
      API.Staff.StaffFrameCopyTaskListParams,
      "user_id" | "created_from" | "created_to"
    >,
    API.Staff.StaffFrameCopyTaskItem
  >({
    fetchPage: listStaffFrameCopyTasks,
    initialFilters: {},
    errorMessage: "视频复刻任务加载失败，请稍后重试",
  })
  const rawTaskRequest = useRef<AbortController | null>(null)
  const [rawTaskModal, setRawTaskModal] = useState<RawTaskModalState | null>(
    null,
  )
  const [rawTaskLoadingId, setRawTaskLoadingId] = useState<string | null>(null)
  const [promptResult, setPromptResult] = useState<string | null>(null)

  useEffect(() => {
    return () => rawTaskRequest.current?.abort()
  }, [])

  const openRawTask = async (stepLabel: string, rawTaskId: string) => {
    rawTaskRequest.current?.abort()
    const controller = new AbortController()
    rawTaskRequest.current = controller
    setRawTaskLoadingId(rawTaskId)

    try {
      const response = await getStaffRawTaskDetail(
        { raw_task_id: rawTaskId },
        controller.signal,
      )

      if (controller.signal.aborted) return

      if (!response.data) {
        void message.warning("未查询到 RawTask 详情")
        return
      }

      setRawTaskModal({ stepLabel, rawTask: response.data })
    } catch (error) {
      if (!isAbortError(error)) {
        // 后端业务错误已由请求层统一提示
        if (!(error instanceof ApiRequestError)) {
          void message.warning("RawTask 详情加载失败，请稍后重试")
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setRawTaskLoadingId(null)
        rawTaskRequest.current = null
      }
    }
  }

  const columns: TableProps<API.Staff.StaffFrameCopyTaskItem>["columns"] = [
    {
      title: "创建时间",
      dataIndex: "created_t",
      fixed: "left",
      width: 180,
      render: formatDateTime,
    },
    {
      title: "用户 ID",
      dataIndex: "user_id",
      fixed: "left",
      width: 220,
      render: renderId,
    },
    {
      title: "任务 ID",
      dataIndex: "id",
      width: 220,
      render: renderId,
    },
    {
      title: "任务状态",
      dataIndex: "status",
      width: 140,
      render: renderFrameCopyStatus,
    },
    {
      title: "抽帧",
      dataIndex: "split_frame",
      width: 210,
      render: (step: API.FrameCopy.SplitFrameInfo) => (
        <StepCell
          meta={[
            `区间：${step.start_time}s - ${step.end_time}s`,
            `时长：${step.duration || step.end_time - step.start_time}s`,
            `帧数：${step.frames?.length || 0}`,
          ]}
          status={step.status}
          statusType="step"
        />
      ),
    },
    {
      title: "渲染分镜图",
      dataIndex: "blend_frame",
      width: 220,
      render: (step?: API.Staff.StaffBlendFrameInfo | null) =>
        step ? (
          <StepCell
            errorMessage={step.error_message}
            meta={[
              step.provider ? `供应商：${step.provider}` : "",
              step.model ? `模型：${step.model}` : "",
              `结果图：${step.result_urls?.length || 0} 张`,
              `完成：${formatDateTime(step.finished_t)}`,
            ]}
            rawTaskId={step.raw_task_id}
            rawTaskLoading={rawTaskLoadingId === step.raw_task_id}
            status={step.status}
            statusType="image"
            onOpenRawTask={(rawTaskId) =>
              void openRawTask("blend_frame", rawTaskId)
            }
          />
        ) : (
          "-"
        ),
    },
    {
      title: "渲染场景图",
      dataIndex: "generate_scene_info",
      width: 230,
      render: (step?: API.Staff.StaffGenerateSceneInfo | null) =>
        step ? (
          <StepCell
            errorMessage={step.error_message}
            meta={[
              step.provider ? `供应商：${step.provider}` : "",
              step.model ? `模型：${step.model}` : "",
              `结果图：${step.result_urls?.length || 0} 张`,
              `完成：${formatDateTime(step.finished_t)}`,
            ]}
            rawTaskId={step.raw_task_id}
            rawTaskLoading={rawTaskLoadingId === step.raw_task_id}
            status={step.status}
            statusType="image"
            onOpenRawTask={(rawTaskId) =>
              void openRawTask("generate_scene_info", rawTaskId)
            }
          />
        ) : (
          "-"
        ),
    },
    {
      title: "生成提示词",
      dataIndex: "blend_prompt",
      width: 250,
      render: (step?: API.Staff.StaffBlendPromptInfo | null) =>
        step ? (
          <Flex
            vertical
            gap={4}
          >
            <StepCell
              errorMessage={step.error_message}
              meta={[
                step.provider ? `供应商：${step.provider}` : "",
                step.model ? `模型：${step.model}` : "",
                `时长：${step.duration}s`,
              ]}
              rawTaskId={step.raw_task_id}
              rawTaskLoading={rawTaskLoadingId === step.raw_task_id}
              status={step.status}
              statusType="step"
              onOpenRawTask={(rawTaskId) =>
                void openRawTask("blend_prompt", rawTaskId)
              }
            />
            {step.result_prompt ? (
              <Button
                className={styles.rawTaskButton}
                size="small"
                type="link"
                onClick={() => setPromptResult(step.result_prompt)}
              >
                查看生成结果
              </Button>
            ) : null}
          </Flex>
        ) : (
          "-"
        ),
    },
    {
      title: "渲染分镜图线稿",
      dataIndex: "blend_frame_artline",
      width: 230,
      render: (step?: API.Staff.StaffGenerateBlendFrameArtlineInfo | null) =>
        step ? (
          <StepCell
            errorMessage={step.error_message}
            meta={[
              step.provider ? `供应商：${step.provider}` : "",
              step.model ? `模型：${step.model}` : "",
              `结果图：${step.result_urls?.length || 0} 张`,
              `完成：${formatDateTime(step.finished_t)}`,
            ]}
            rawTaskId={step.raw_task_id}
            rawTaskLoading={rawTaskLoadingId === step.raw_task_id}
            status={step.status}
            statusType="image"
            onOpenRawTask={(rawTaskId) =>
              void openRawTask("blend_frame_artline", rawTaskId)
            }
          />
        ) : (
          "-"
        ),
    },
    {
      title: "生成视频",
      dataIndex: "create_video",
      width: 250,
      render: (step?: API.Staff.StaffFrameCopyCreateVideoInfo | null) =>
        step ? (
          <Flex
            vertical
            gap={4}
          >
            <StepCell
              errorMessage={step.error_message}
              meta={[
                step.provider ? `供应商：${step.provider}` : "",
                step.model ? `模型：${step.model}` : "",
                `${step.duration}s · ${step.resolution} · ${step.ratio}`,
                `完成：${formatDateTime(step.finished_t)}`,
              ]}
              rawTaskId={step.raw_task_id}
              rawTaskLoading={rawTaskLoadingId === step.raw_task_id}
              status={step.status}
              statusType="step"
              onOpenRawTask={(rawTaskId) =>
                void openRawTask("create_video", rawTaskId)
              }
            />
            {step.video_url ? (
              <Link
                href={step.video_url}
                rel="noreferrer"
                target="_blank"
              >
                查看生成视频
              </Link>
            ) : null}
          </Flex>
        ) : (
          "-"
        ),
    },
  ]

  const applyFilters = async () => {
    const values = await form.validateFields()
    const createdRange = values.createdRange

    search({
      user_id: optionalValue(values.userId),
      created_from: createdRange?.[0].toISOString(),
      created_to: createdRange?.[1].toISOString(),
    })
  }

  const resetFilters = () => {
    form.resetFields()
    search({})
  }

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>视频复刻任务</Title>
        <Text type="secondary">
          查看 FrameCopyTask 各处理步骤，并检查关联 RawTask
          的执行详情与模型成本。
        </Text>
      </Flex>

      <section className={styles.filterPanel}>
        <Form<FrameCopyTaskFilterForm>
          form={form}
          layout="vertical"
          onFinish={applyFilters}
        >
          <Flex
            align="flex-end"
            gap={16}
            wrap
          >
            <Form.Item
              className={styles.userIdField}
              label="用户 ID"
              name="userId"
            >
              <Input
                allowClear
                placeholder="请输入用户 ID（精准匹配）"
              />
            </Form.Item>
            <Form.Item
              className={styles.dateRangeField}
              label="任务创建时间"
              name="createdRange"
            >
              <RangePicker
                allowClear
                format="YYYY-MM-DD HH:mm"
                placeholder={["开始时间", "结束时间"]}
                showTime={{ format: "HH:mm" }}
              />
            </Form.Item>
            <Form.Item className={styles.actionField}>
              <Flex gap={10}>
                <Button onClick={resetFilters}>重置</Button>
                <Button
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                  type="primary"
                >
                  查询
                </Button>
              </Flex>
            </Form.Item>
          </Flex>
        </Form>
      </section>

      <section className={styles.listPanel}>
        <Flex
          className={styles.listHeader}
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <CodeOutlined className={styles.sectionIcon} />
            <Title level={4}>FrameCopyTask 记录</Title>
            <Text type="secondary">共 {total} 条</Text>
          </Flex>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => refresh()}
          >
            刷新
          </Button>
        </Flex>
        <Table
          columns={columns}
          {...tableProps}
          rowKey={(record) =>
            record.id || `${record.user_id}-${record.created_t || "unknown"}`
          }
          scroll={{ x: "max-content" }}
        />
      </section>

      <RawTaskModal
        modalState={rawTaskModal}
        onClose={() => setRawTaskModal(null)}
      />
      <Modal
        footer={null}
        open={Boolean(promptResult)}
        title="生成提示词结果"
        width={760}
        onCancel={() => setPromptResult(null)}
      >
        <Paragraph
          className={styles.promptResult}
          copyable={promptResult ? { text: promptResult } : false}
        >
          {promptResult}
        </Paragraph>
      </Modal>
    </Flex>
  )
}
